import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBooking, useBookingStatusMutation } from '../api';
import { PageLoader, ErrorState, StatusBadge } from '../../components/ui';
import { money, dateTime, weight } from '../../lib/format';
import { useToast } from '../../components/Toast';
import { ApiRequestError } from '../../lib/api';
import type { BookingStatus } from '../../lib/types';

const NEXT: Record<BookingStatus, BookingStatus[]> = {
  New: ['Contacted', 'Confirmed', 'Cancelled'],
  Contacted: ['Confirmed', 'Processing', 'Cancelled'],
  Confirmed: ['Processing', 'Completed', 'Cancelled'],
  Processing: ['Completed', 'Cancelled'],
  Completed: [],
  Cancelled: [],
};

export function BookingDetailPage() {
  const { id } = useParams();
  const { data: b, isLoading, error, refetch } = useBooking(id);
  const mut = useBookingStatusMutation();
  const toast = useToast();
  const [note, setNote] = useState('');

  if (isLoading) return <PageLoader />;
  if (error || !b) return <ErrorState message={(error as Error)?.message ?? 'Not found'} onRetry={() => refetch()} />;

  const changeStatus = async (status: BookingStatus) => {
    try {
      await mut.mutateAsync({ id: b._id, status, note: note || undefined });
      setNote('');
      toast(`Marked ${status}`, 'success');
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Update failed', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/admin/bookings" className="text-ink-400">
          ‹ Bookings
        </Link>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-mono text-2xl font-semibold text-ink-900">{b.bookingNumber}</h1>
          <p className="text-sm text-ink-400">Placed {dateTime(b.createdAt)}</p>
        </div>
        <StatusBadge status={b.status} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-4 lg:col-span-2">
          <h2 className="mb-3 text-sm font-semibold text-ink-700">Items</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="table-head">
                <th className="px-2 py-1.5">Product</th>
                <th className="px-2 py-1.5">Stall / Rack</th>
                <th className="px-2 py-1.5 text-right">Qty</th>
                <th className="px-2 py-1.5 text-right">Gold weight</th>
                <th className="px-2 py-1.5 text-right">Unit</th>
                <th className="px-2 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {b.items.map((it, i) => (
                <tr key={i}>
                  <td className="px-2 py-2">
                    <div className="font-medium">{it.productName}</div>
                    <div className="font-mono text-xs text-ink-400">
                      #{it.barcodeNo}
                      {it.size ? ` · ${it.size}` : ''}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-xs text-ink-500">
                    {it.stallNumber} / {it.rackNumber}
                  </td>
                  <td className="px-2 py-2 text-right">{it.quantity}</td>
                  <td className="px-2 py-2 text-right text-ink-500">{weight(it.grossWeight)} each</td>
                  <td className="px-2 py-2 text-right">{money(it.unitPrice, true)}</td>
                  <td className="px-2 py-2 text-right font-medium">{money(it.lineTotal, true)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-ink-100">
                <td className="px-2 py-2 font-medium" colSpan={2}>
                  Total ({b.totalQuantity} pcs)
                </td>
                <td className="px-2 py-2 text-right font-semibold text-gold-600">{weight(b.totalGrossWeight)}</td>
                <td />
                <td className="px-2 py-2 text-right text-base font-semibold">{money(b.totalAmount, true)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="space-y-4">
          <div className="card p-4">
            <h2 className="mb-2 text-sm font-semibold text-ink-700">Visitor</h2>
            <dl className="space-y-1 text-sm">
              <Info label="Name" value={b.visitorName} />
              <Info label="Mobile" value={b.visitorMobile} />
              {b.visitorEmail && <Info label="Email" value={b.visitorEmail} />}
              {b.visitorCompany && <Info label="Company" value={b.visitorCompany} />}
              {b.visitorNote && <Info label="Note" value={b.visitorNote} />}
            </dl>
          </div>

          <div className="card p-4">
            <h2 className="mb-2 text-sm font-semibold text-ink-700">Update status</h2>
            {NEXT[b.status].length === 0 ? (
              <p className="text-sm text-ink-400">This booking is {b.status.toLowerCase()} and closed.</p>
            ) : (
              <>
                <input
                  className="input mb-2"
                  placeholder="Optional note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  {NEXT[b.status].map((s) => (
                    <button
                      key={s}
                      className={s === 'Cancelled' ? 'btn-danger' : 'btn-outline'}
                      disabled={mut.isPending}
                      onClick={() => changeStatus(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="mb-3 text-sm font-semibold text-ink-700">Status history</h2>
        <ol className="space-y-2">
          {b.statusHistory.map((h, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <StatusBadge status={h.status} />
              <span className="text-ink-400">{dateTime(h.at)}</span>
              <span className="text-ink-500">by {h.byLabel}</span>
              {h.note && <span className="text-ink-400">— {h.note}</span>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-400">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}
